import { useSyncExternalStore } from "react";

// One global action history for the open project (table, PDF, comments, AI, Binder).
const LIMIT = 500;
const ATTACH_MS = 400;
let past = [];
let future = [];
let busy = false;
let lastPushAt = 0;
let pending = [];
let pendingTimer = null;
let group = null;
let version = 0;
const listeners = new Set();
const notify = () => { version += 1; listeners.forEach((l) => l()); };

const compound = (main, subs) => (!subs.length ? main : {
    label: main.label,
    undo: async () => { for (const s of [...subs].reverse()) await s.undo(); await main.undo(); },
    redo: async () => { await main.redo(); for (const s of subs) await s.redo(); },
});

const flushPending = () => {
    clearTimeout(pendingTimer); pendingTimer = null;
    if (!pending.length) return;
    const [first, ...rest] = pending; pending = [];
    commit(compound(first, rest));
};

function commit(entry) {
    past.push(entry);
    if (past.length > LIMIT) past.shift();
    future = [];
    lastPushAt = Date.now();
    notify();
}

export const history = {
    // Regular action. Entries may be updated later via the returned handle (drag / typing sessions).
    push(entry) {
        if (busy) return null;
        if (group) { group.entries.push(entry); return entry; }
        let e = entry;
        if (pending.length) { e = compound(entry, pending); pending = []; clearTimeout(pendingTimer); pendingTimer = null; }
        commit(e);
        return e;
    },
    // Side effect of the action just done / about to be done (e.g. AI learning example) - merged into that step.
    attach(sub) {
        if (busy) return;
        if (group) { group.entries.push(sub); return; }
        if (past.length && Date.now() - lastPushAt < ATTACH_MS) { past[past.length - 1] = compound(past[past.length - 1], [sub]); notify(); return; }
        pending.push(sub);
        clearTimeout(pendingTimer);
        pendingTimer = setTimeout(flushPending, ATTACH_MS);
    },
    begin(label) { if (!busy) group = { label, entries: [] }; },
    end() {
        const g = group; group = null;
        if (!g || !g.entries.length) return;
        const [first, ...rest] = g.entries;
        commit({ ...compound(first, rest), label: g.label || first.label });
    },
    replaceLast(entry) { if (past.length) { past[past.length - 1] = entry; notify(); } },
    last: () => past[past.length - 1] || null,
    async undo() {
        flushPending();
        const e = past.pop();
        if (!e) return null;
        busy = true;
        try { await e.undo(); } catch (err) { past.push(e); notify(); throw err; } finally { busy = false; }
        future.push(e);
        notify();
        return e;
    },
    async redo() {
        const e = future.pop();
        if (!e) return null;
        busy = true;
        try { await e.redo(); } catch (err) { future.push(e); notify(); throw err; } finally { busy = false; }
        past.push(e);
        notify();
        return e;
    },
    isBusy: () => busy,
    clear() { past = []; future = []; pending = []; group = null; notify(); },
    subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); },
    labels: () => ({ undo: past[past.length - 1]?.label || null, redo: future[future.length - 1]?.label || null, size: past.length }),
};

export function useHistoryLabels() {
    useSyncExternalStore(history.subscribe, () => version);
    return history.labels();
}
